import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FileArchive, Files, FileUp, FolderUp, UploadCloud } from "lucide-react";

import { moveSource, removeSource, safeErrorMessage, uploadSources, type DatasetLocation } from "./api";
import type { ManagedSource, SourceRevision, SourceRoleContract, SourcesResponse } from "./types";
import { useImportQueue, type QueuedImportFile } from "./useImportQueue";
import { DataManagementStickyActions } from "./DataManagementStickyActions";
import { SourceCategorySummary } from "./SourceCategorySummary";
import { SourceFilesDrawer } from "./SourceFilesDrawer";
import { SourceSectionNavigation } from "./SourceSectionNavigation";
import { categoryStats, type SourceAnalysisState } from "./source-explorer-model";

type ExplorerProps = {
  location: DatasetLocation;
  data: SourcesResponse;
  revisions: SourceRevision[];
  onChanged: () => Promise<void>;
  onValidate?: () => void;
  validationBusy?: boolean;
  activeProcessing?: { id: string; state: string; progress: number; step: string | null } | null;
  onCancelProcessing?: () => void;
  display?: "summary" | "editor";
  initialImport?: SourceImportSelection | null;
};

export type SourceImportSelection = {
  files: File[];
  role: string;
  mode: "files" | "folder" | "zip";
};

export function SourceExplorer(props: ExplorerProps) {
  if (props.display === "editor") return <SourceExplorerEditor {...props} />;
  return <SourceExplorerSummary {...props} />;
}

function SourceExplorerEditor({ location, data, revisions, onChanged, onValidate, validationBusy = false, activeProcessing = null, onCancelProcessing, initialImport = null }: ExplorerProps) {
  const roles = useMemo(() => sourceRoles(data), [data]);
  const [uploadRole, setUploadRole] = useState(roles.find((role) => role.providedBy === "user")?.role ?? "");
  const [files, setFiles] = useState<File[]>([]);
  const [uploadMode, setUploadMode] = useState<"files" | "folder" | "zip">("files");
  const [replacement, setReplacement] = useState<ManagedSource | null>(null);
  const [drawerRole, setDrawerRole] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [error, setError] = useState("");
  const controller = useRef<AbortController | null>(null);
  const replacementCollection = useRef<ManagedSource[]>([]);
  useEffect(() => {
    if (!initialImport) return;
    replacementCollection.current = [];
    setReplacement(null);
    setFiles(initialImport.files);
    setUploadMode(initialImport.mode);
    setError("");
    if (initialImport.role) setUploadRole(initialImport.role);
  }, [initialImport]);
  const afterSessionImport = useCallback(async (completed?: { completedRevision: number | null }) => {
    const previous = replacementCollection.current;
    if (previous.length && completed?.completedRevision) {
      let expectedRevision = completed.completedRevision;
      try {
        for (const source of previous) {
          await removeSource(location, source.id, expectedRevision);
          expectedRevision += 1;
        }
        replacementCollection.current = [];
      } catch (cause) {
        setError(safeErrorMessage(cause));
      }
    }
    await onChanged();
  }, [location, onChanged]);
  const importQueue = useImportQueue(location, afterSessionImport);
  const groupedSources = useMemo(() => {
    const grouped = new Map<string, ManagedSource[]>();
    for (const role of roles) grouped.set(role.role, []);
    for (const source of data.sources) grouped.get(source.role)?.push(source);
    return grouped;
  }, [data.sources, roles]);
  const invalidSourceIds = useMemo(() => new Set(data.readiness?.invalidSourceIds ?? []), [data.readiness?.invalidSourceIds]);
  const upload = useMutation({
    mutationFn: async () => {
      controller.current = new AbortController();
      return await uploadSources(
        location,
        files,
        controller.current.signal,
        replacement?.role ?? uploadRole,
        data.sourceRevision,
        replacement?.id,
      );
    },
    onSuccess: async () => {
      setFiles([]);
      setReplacement(null);
      setError("");
      await onChanged();
    },
    onError: (cause) => setError(safeErrorMessage(cause)),
    onSettled: () => { controller.current = null; },
  });
  const mutation = useMutation({
    mutationFn: async (action: { kind: "move"; source: ManagedSource; role: string } | { kind: "remove"; sources: ManagedSource[] }) => {
      if (action.kind === "move") return await moveSource(location, action.source.id, action.role, data.sourceRevision);
      let expectedRevision = data.sourceRevision;
      for (const source of action.sources) {
        await removeSource(location, source.id, expectedRevision);
        expectedRevision += 1;
      }
      return undefined;
    },
    onSuccess: onChanged,
    onError: (cause) => setError(safeErrorMessage(cause)),
  });
  const effectiveRole = replacement?.role ?? uploadRole;
  const effectiveContract = roles.find((role) => role.role === effectiveRole);
  const useSessionImport = !replacement && Boolean(data.contract && effectiveContract?.multiple);
  const importLocked = Boolean(importQueue.session?.resumable);
  const invalidSelection = selectedFileError(files, data, effectiveRole);
  const submitUpload = () => {
    if (!useSessionImport) {
      upload.mutate();
      return;
    }
    if (importQueue.session?.resumable) {
      importQueue.resumeWithFiles(files);
      setFiles([]);
      return;
    }
    void importQueue.start(files, effectiveRole, uploadMode, data.sourceRevision).then(() => {
      setFiles([]);
      setError("");
    }).catch((cause) => setError(safeErrorMessage(cause)));
  };
  const openUpload = (role: string) => {
    replacementCollection.current = [];
    setReplacement(null);
    setUploadRole(role);
    setUploadOpen(true);
  };
  const replaceCollection = (role: SourceRoleContract, sources: ManagedSource[]) => {
    if (!window.confirm(`Remplacer les ${sources.length} fichiers de “${role.label}” après l’import réussi de la nouvelle collection ?`)) return;
    replacementCollection.current = sources;
    setReplacement(null);
    setUploadRole(role.role);
    setUploadOpen(true);
  };
  const removeSources = (sources: ManagedSource[]) => {
    if (!sources.length) return;
    const message = sources.length === 1
      ? `Retirer ${sources[0].originalFilename} de la préparation ?`
      : `Retirer les ${sources.length} fichiers sélectionnés de la préparation ?`;
    if (!window.confirm(message)) return;
    mutation.mutate({ kind: "remove", sources });
  };
  const downloadInventory = (role: SourceRoleContract, sources: ManagedSource[]) => {
    const lines = ["name,relative_path,extension,size_bytes,status,created_at"];
    for (const source of sources) {
      lines.push([source.originalFilename, source.virtualPath || source.originalFilename, fileExtension(source.originalFilename), source.sizeBytes, source.status, source.createdAt].map(csvCell).join(","));
    }
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `inventaire-${role.role}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const selectedDrawerRole = roles.find((role) => role.role === drawerRole) ?? null;
  const drawerSources = selectedDrawerRole ? groupedSources.get(selectedDrawerRole.role) ?? [] : [];
  const cancelImport = () => {
    if (!importQueue.session?.cancellable && activeProcessing) {
      onCancelProcessing?.();
      return;
    }
    replacementCollection.current = [];
    controller.current?.abort();
    void importQueue.cancel();
  };
  return <section className="source-explorer-editor" aria-label="Ajouter ou remplacer des sources">
    <SourceUploadDialog
      roles={roles}
      role={effectiveRole}
      files={files}
      replacement={replacement}
      pending={upload.isPending || importQueue.running || importLocked}
      error={error || importQueue.error || invalidSelection}
      onRole={setUploadRole}
      onFiles={(selected, mode) => { setReplacement(null); setFiles(selected); setUploadMode(mode); }}
      onUpload={submitUpload}
      onCancel={cancelImport}
    />
    <ImportQueuePanel queue={importQueue} onReselect={importQueue.resumeWithFiles} />
  </section>;
}

function SourceExplorerSummary({ location, data, revisions, activeProcessing = null }: ExplorerProps) {
  const roles = sourceRoles(data);
  const groupedSources = new Map<string, ManagedSource[]>();
  for (const role of roles) groupedSources.set(role.role, []);
  for (const source of data.sources) groupedSources.get(source.role)?.push(source);
  const invalidSourceIds = new Set(data.readiness?.invalidSourceIds ?? []);
  return <div className="source-explorer">
    <div className="source-explorer-launch"><a className="btn btn-primary" href={`/clients/${encodeURIComponent(location.clientId)}/datasets/${encodeURIComponent(location.datasetId)}/source-explorer?mode=read`}>Ouvrir dans l’explorateur</a><span>Parcourir les dossiers, révisions, métadonnées et aperçus.</span></div>
    <SourceSectionNavigation roles={roles} />
    <SourceReadinessSummary data={data} />
    <div className="source-category-list">
      {roles.map((role) => {
        const sources = groupedSources.get(role.role) ?? [];
        const processingThisRole = Boolean(activeProcessing && role.analysisRequired);
        const analysis = processingThisRole ? "running" : effectiveAnalysisState(role.role, data, null);
        const progress = processingThisRole
          ? processingAnalysisProgress(activeProcessing)
          : "";
        const stats = categoryStats(role, sources, analysis, invalidSourceIds, progress);
        const href = `/clients/${encodeURIComponent(location.clientId)}/datasets/${encodeURIComponent(location.datasetId)}/source-explorer?mode=read&role=${encodeURIComponent(role.role)}`;
        return <SourceCategorySummary key={role.role} role={role} sources={sources} stats={stats} explorerHref={href} />;
      })}
    </div>
    <SourceRevisionHistory revisions={revisions} />
  </div>;
}

export function SourceUploadDialog({ roles, role, files, replacement, pending, error, onRole, onFiles, onUpload, onCancel }: {
  roles: SourceRoleContract[]; role: string; files: File[]; replacement: ManagedSource | null;
  pending: boolean; error: string; onRole: (role: string) => void;
  onFiles: (files: File[], mode: "files" | "folder" | "zip") => void;
  onUpload: () => void; onCancel: () => void;
}) {
  const userRoles = roles.filter((item) => item.providedBy === "user" && item.compatible !== false);
  const selectedRole = userRoles.find((item) => item.role === role);
  const regularExtensions = selectedRole?.extensions.filter((extension) => extension !== ".zip") ?? [];
  const recommendation = replacement ? null : fileRecommendation(files, userRoles, role);
  const actionLabel = uploadActionLabel(pending, replacement);
  const [dropActive, setDropActive] = useState(false);
  return <section className="source-upload" aria-label="Importer des sources">
    <header className="source-upload-heading">
      <span className="source-upload-heading-icon"><UploadCloud size={24} aria-hidden="true" /></span>
      <div><h3>{replacement ? `Remplacer ${replacement.originalFilename}` : "Ajouter à la préparation"}</h3><p>Choisissez une catégorie, puis ajoutez vos fichiers.</p></div>
    </header>
    {!replacement ? <label className="source-upload-role">Catégorie métier<select value={role} onChange={(event) => onRole(event.target.value)}>{userRoles.map((item) => <option key={item.role} value={item.role}>{item.label}</option>)}</select></label> : null}
    <div className="source-import-actions">
      {selectedRole && regularExtensions.length ? <label className="btn btn-primary"><FileUp size={17} aria-hidden="true" />{addFilesLabel(selectedRole, regularExtensions)}<input className="source-hidden-input" type="file" multiple={selectedRole.multiple} disabled={pending} accept={regularExtensions.join(",")} onChange={(event) => onFiles(Array.from(event.target.files ?? []), "files")} /></label> : null}
      {selectedRole?.multiple && regularExtensions.length ? <label className="btn btn-outline"><FolderUp size={17} aria-hidden="true" />Importer un dossier<input className="source-hidden-input" type="file" multiple disabled={pending} accept={regularExtensions.join(",")} ref={(input) => { if (input) input.setAttribute("webkitdirectory", ""); }} onChange={(event) => onFiles(Array.from(event.target.files ?? []), "folder")} /></label> : null}
      {selectedRole?.extensions.includes(".zip") ? <label className="btn btn-outline"><FileArchive size={17} aria-hidden="true" />Importer une archive ZIP<input className="source-hidden-input" type="file" accept=".zip" disabled={pending} onChange={(event) => onFiles(Array.from(event.target.files ?? []), "zip")} /></label> : null}
    </div>
    <div
      className={`source-drop-zone${dropActive ? " is-dragging" : ""}`}
      onDragEnter={(event) => { event.preventDefault(); setDropActive(true); }}
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; }}
      onDragLeave={() => setDropActive(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDropActive(false);
        const dropped = Array.from(event.dataTransfer.files);
        onFiles(dropped, inferredMode(dropped));
      }}
    >
      <UploadCloud size={32} aria-hidden="true" />
      <strong>Glissez-déposez vos fichiers ici</strong>
      <span>CSV, Excel, dossier ou archive ZIP selon la catégorie</span>
    </div>
    {files.length ? <SelectedFilesPreview files={files} /> : null}
    {recommendation ? <SourceRecommendation recommendation={recommendation} onRole={onRole} /> : null}
    {error ? <p className="dm-error" role="alert">{error}</p> : null}
    <div className="dm-actions"><button className="btn btn-primary" type="button" disabled={!files.length || Boolean(error) || pending || !role} onClick={onUpload}>{actionLabel}</button>{pending ? <button className="btn btn-outline" type="button" onClick={onCancel}>Annuler</button> : null}</div>
  </section>;
}

function SelectedFilesPreview({ files }: { files: File[] }) {
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");
  const totalBytes = files.reduce((total, file) => total + file.size, 0);
  const formats = [...new Set(files.map((file) => fileExtension(file.name) || "sans extension"))];
  const filtered = files.filter((file) => (file.webkitRelativePath || file.name).toLowerCase().includes(search.toLowerCase()));
  return <div className="source-selection-summary">
    <div className="source-selection-heading"><Files size={20} aria-hidden="true" /><p><strong>{selectedFileCountLabel(files.length)}</strong><span>{formatBytes(totalBytes)} · {formats.join(", ")}</span></p></div>
    {!showAll ? <ul>{files.slice(0, 20).map((file, index) => <li key={`${index}-${file.name}`}>{file.webkitRelativePath || file.name} · {formatBytes(file.size)}</li>)}</ul> : null}
    {files.length > 20 ? <button className="btn btn-outline" type="button" onClick={() => setShowAll((value) => !value)}>{showAll ? "Réduire la liste" : "Afficher tous les fichiers"}</button> : null}
    {showAll ? <><label>Rechercher<input value={search} onChange={(event) => setSearch(event.target.value)} /></label><VirtualFileList files={filtered} /></> : null}
  </div>;
}

function selectedFileCountLabel(count: number) {
  if (count === 1) return "1 fichier sélectionné";
  return `${count} fichiers sélectionnés`;
}

function VirtualFileList({ files }: { files: File[] }) {
  const [scrollTop, setScrollTop] = useState(0);
  const rowHeight = 32;
  const viewportHeight = 320;
  const visibleRows = Math.ceil(viewportHeight / rowHeight) + 4;
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - 2);
  const visible = files.slice(start, start + visibleRows);
  return <div className="source-virtual-list" style={{ height: viewportHeight, overflowY: "auto" }} onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}>
    <div style={{ height: files.length * rowHeight, position: "relative" }}>
      {visible.map((file, offset) => <div key={`${start + offset}-${file.name}`} className="source-virtual-row" style={{ position: "absolute", top: (start + offset) * rowHeight, height: rowHeight }}>{file.webkitRelativePath || file.name} · {formatBytes(file.size)}</div>)}
    </div>
  </div>;
}

function ImportQueuePanel({ queue, onReselect }: {
  queue: ReturnType<typeof useImportQueue>;
  onReselect: (files: File[]) => void;
}) {
  const [showFiles, setShowFiles] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<QueuedImportFile["status"] | "all">("all");
  const session = queue.session;
  if (!session) return null;
  const selectedFilesAvailable = queue.entries.length > 0;
  const failed = queue.entries.filter((entry) => entry.status === "error");
  const visibleEntries = queue.entries.filter((entry) => {
    if (status !== "all" && entry.status !== status) return false;
    const path = entry.file.webkitRelativePath || entry.file.name;
    return path.toLowerCase().includes(search.toLowerCase());
  });
  return <section className="source-import-progress" aria-label="Progression de l’import">
    <h3>Import des sources</h3>
    <p>Envoi : {session.readyFiles} / {session.expectedFiles} fichiers — {session.progress} %</p>
    <progress value={session.progress} max="100">{session.progress} %</progress>
    {!selectedFilesAvailable && session.resumable ? <label className="btn btn-outline">Sélectionner de nouveau le dossier<input className="source-hidden-input" type="file" multiple ref={(input) => { if (input) input.setAttribute("webkitdirectory", ""); }} onChange={(event) => onReselect(Array.from(event.target.files ?? []))} /></label> : null}
    {failed.length ? <p className="dm-error">{failed.length} fichier(s) en erreur. {failed.slice(0, 3).map((entry) => entry.file.name).join(", ")}</p> : null}
    {selectedFilesAvailable ? <button className="btn btn-outline" type="button" onClick={() => setShowFiles((value) => !value)}>{showFiles ? "Masquer les fichiers" : "Afficher les fichiers de l’import"}</button> : null}
    {showFiles ? <div className="source-import-file-browser">
      <label>Rechercher<input value={search} onChange={(event) => setSearch(event.target.value)} /></label>
      <label>État<select value={status} onChange={(event) => setStatus(event.target.value as QueuedImportFile["status"] | "all")}><option value="all">Tous</option><option value="pending">En attente</option><option value="uploading">En cours</option><option value="ready">Vérifiés</option><option value="error">En erreur</option><option value="cancelled">Annulés</option></select></label>
      <VirtualQueueFileList entries={visibleEntries} />
    </div> : null}
    {session.analysis ? <><p>Analyse : {analysisProgressLabel(session.analysis.step, session.analysis.progress)}</p><progress value={session.analysis.progress} max="100">{session.analysis.progress} %</progress></> : null}
    {queue.reconnecting ? <p className="dm-reconnecting" role="status">Connexion temporairement interrompue — suivi de l’import en reprise…</p> : null}
    <div className="dm-actions">
      {queue.running && !queue.paused ? <button className="btn btn-outline" type="button" onClick={queue.pause}>Mettre en pause</button> : null}
      {queue.paused ? <button className="btn btn-outline" type="button" onClick={queue.resume}>Reprendre</button> : null}
      {failed.length ? <button className="btn btn-outline" type="button" onClick={queue.retry}>Réessayer les fichiers en erreur</button> : null}
      {session.cancellable ? <button className="btn btn-outline" type="button" onClick={() => void queue.cancel()}>{session.analysis ? "Abandonner le traitement" : "Annuler l’import"}</button> : null}
    </div>
  </section>;
}

function VirtualQueueFileList({ entries }: { entries: QueuedImportFile[] }) {
  const [scrollTop, setScrollTop] = useState(0);
  const rowHeight = 32;
  const viewportHeight = 320;
  const visibleRows = Math.ceil(viewportHeight / rowHeight) + 4;
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - 2);
  const visible = entries.slice(start, start + visibleRows);
  return <div className="source-virtual-list" style={{ height: viewportHeight, overflowY: "auto" }} onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}>
    <div style={{ height: entries.length * rowHeight, position: "relative" }}>
      {visible.map((entry, offset) => <div key={entry.index} className="source-virtual-row" style={{ position: "absolute", top: (start + offset) * rowHeight, height: rowHeight }}>{entry.file.webkitRelativePath || entry.file.name} · {queueStatusLabel(entry.status)}</div>)}
    </div>
  </div>;
}

function queueStatusLabel(status: QueuedImportFile["status"]) {
  if (status === "pending") return "en attente";
  if (status === "uploading") return "en cours";
  if (status === "ready") return "vérifié";
  if (status === "error") return "en erreur";
  return "annulé";
}

function inferredMode(files: File[]): "files" | "folder" | "zip" {
  if (files.length === 1 && fileExtension(files[0].name) === ".zip") return "zip";
  if (files.some((file) => Boolean(file.webkitRelativePath))) return "folder";
  return "files";
}

function analysisProgressLabel(step: string | null, progress: number) {
  if (step) return `${step} — ${progress} %`;
  return `${progress} %`;
}

function SourceRecommendation({ recommendation, onRole }: {
  recommendation: { message: string; role: SourceRoleContract | null };
  onRole: (role: string) => void;
}) {
  const chooseRole = () => {
    if (recommendation.role) onRole(recommendation.role.role);
  };
  return <div className="source-recommendation" role="status">
    <p>{recommendation.message}</p>
    {recommendation.role ? <button className="btn btn-outline" type="button" onClick={chooseRole}>Utiliser {recommendation.role.label}</button> : null}
  </div>;
}

export function SourceReadinessSummary({ data }: { data: SourcesResponse }) {
  const readiness = data.readiness;
  if (!readiness) return null;
  return <section className="source-readiness" id="source-summary"><h3>État de la préparation r{data.sourceRevision}</h3><dl className="dm-summary-grid"><Summary label="Manifeste" value={readiness.ready ? "Prêt à valider" : "Incomplet"} /><Summary label="Fichiers" value={`${readiness.totalFiles}`} /><Summary label="Rapports" value={`${readiness.reportCount}`} /><Summary label="Taille totale" value={formatBytes(Number(readiness.totalSizeBytes))} /></dl>{readiness.missingRoles.length ? <p className="dm-warning">Rôles manquants : {readiness.missingRoles.join(", ")}</p> : null}{readiness.invalidSourceIds.length ? <p className="dm-error">{readiness.invalidSourceIds.length} fichier(s) incompatible(s).</p> : null}{readiness.archivePending ? <p>Archive en attente d’analyse par le worker.</p> : null}</section>;
}

export function SourceRevisionHistory({ revisions }: { revisions: SourceRevision[] }) {
  return <section className="dm-revisions" id="source-history"><h3>Historique des révisions</h3>{revisions.length ? revisions.map((revision) => <details key={revision.id ?? revision.sourceRevision}><summary>r{revision.sourceRevision} · {revision.fileCount ?? revision.files.length} source(s) · {formatDate(revision.capturedAt)}</summary><p>{revision.authorName || "Système"} · {revision.action || "capture"} · validation {revision.validationStatus || "non lancée"}</p>{revision.files.length ? <><p>Ajoutées : {revision.addedSourceIds?.length ?? 0} · retirées : {revision.removedSourceIds?.length ?? 0} · remplacées : {revision.replacedSourceIds?.length ?? 0}</p><ul>{revision.files.slice(0, 5).map((file) => <li key={file.sourceId}>{file.virtualPath || file.originalFilename} · {file.role} · {file.changeType || "capturée"}</li>)}</ul>{revision.files.length > 5 ? <p>{revision.files.length - 5} autre(s) fichier(s) non affiché(s) dans cette vue compacte.</p> : null}</> : <p>Ouvrez l’explorateur pour consulter les fichiers sans charger le manifeste complet.</p>}</details>) : <p className="dm-empty">Aucune révision.</p>}</section>;
}

function sourceRoles(data: SourcesResponse): SourceRoleContract[] {
  if (data.contract?.roles.length) {
    const known = new Set(data.contract.roles.map((role) => role.role));
    const unknown = [...new Set(data.sources.map((source) => source.role).filter((role) => !known.has(role)))];
    return [
      ...data.contract.roles,
      ...unknown.map((role) => ({
        role,
        label: `Fichiers incompatibles (${role})`,
        required: false,
        multiple: true,
        extensions: data.uploadPolicy.allowedExtensions,
        providedBy: "user" as const,
        description: "Ce rôle n’appartient pas au contrat du template.",
        compatible: false,
      })),
    ];
  }
  const names = [...new Set(data.sources.map((source) => source.role))];
  if (!names.length) names.push("primary");
  return names.map((role) => ({ role, label: role, required: false, multiple: true, extensions: data.uploadPolicy.allowedExtensions, providedBy: "user", description: "" }));
}

function selectedFileError(files: File[], data: SourcesResponse, role: string) {
  if (files.length > data.uploadPolicy.maxFiles) return `Sélection limitée à ${data.uploadPolicy.maxFiles} fichiers.`;
  const oversized = files.find((file) => file.size > data.uploadPolicy.maxBytesPerFile);
  if (oversized) return `${oversized.name} dépasse la taille autorisée.`;
  const contract = data.contract?.roles.find((item) => item.role === role);
  const extensions = contract?.extensions ?? data.uploadPolicy.allowedExtensions;
  const invalid = files.find((file) => !extensions.some((extension) => file.name.toLowerCase().endsWith(extension)));
  if (!invalid) return "";
  if (!contract) return `${invalid.name} utilise un format incompatible.`;
  const extension = fileExtension(invalid.name);
  const compatible = compatibleRoles(data.contract?.roles ?? [], extension);
  const format = selectedFormatLabel(extension);
  const agreement = extension === ".zip" ? "ajoutée" : "ajouté";
  const accepted = acceptedFormatsLabel(contract.extensions);
  if (compatible.length === 1) {
    return `${format} ne peut pas être ${agreement} dans “${contract.label}”. Cette catégorie accepte : ${accepted}. Sélectionnez “${compatible[0].label}”.`;
  }
  return `${format} ne peut pas être ${agreement} dans “${contract.label}”. Cette catégorie accepte : ${accepted}.`;
}

function roleExtensions(roles: SourceRoleContract[], role: string) { return roles.find((item) => item.role === role)?.extensions.join(",") || ""; }
function fileRecommendation(files: File[], roles: SourceRoleContract[], currentRole: string) {
  const current = roles.find((role) => role.role === currentRole);
  const file = files.find((candidate) => {
    const extension = fileExtension(candidate.name);
    return extension && !current?.extensions.includes(extension);
  });
  if (!file) return hintedFileRecommendation(files, roles, currentRole);
  const matches = compatibleRoles(roles, fileExtension(file.name));
  if (matches.length === 1) {
    return { message: `${file.name} semble correspondre à ${matches[0].label}.`, role: matches[0] };
  }
  if (matches.length > 1) {
    return { message: `${file.name} correspond à plusieurs catégories : ${matches.map((role) => role.label).join(", ")}. Sélectionnez la catégorie voulue.`, role: null };
  }
  return null;
}

function hintedFileRecommendation(files: File[], roles: SourceRoleContract[], currentRole: string) {
  for (const file of files) {
    const name = file.name.toLowerCase();
    const extension = fileExtension(file.name);
    const matches = roles.filter((role) => {
      if (role.role === currentRole || !role.extensions.includes(extension)) return false;
      return role.filenameHints?.some((hint) => name.includes(hint.toLowerCase())) ?? false;
    });
    if (matches.length === 1) {
      return { message: `${file.name} semble correspondre à ${matches[0].label}.`, role: matches[0] };
    }
  }
  return null;
}
function compatibleRoles(roles: SourceRoleContract[], extension: string) { return roles.filter((role) => role.providedBy === "user" && role.compatible !== false && role.extensions.includes(extension)); }
function fileExtension(filename: string) { const position = filename.lastIndexOf("."); if (position < 0) return ""; return filename.slice(position).toLowerCase(); }
function addFilesLabel(role: SourceRoleContract, extensions: string[]) { const quantity = role.multiple ? "des fichiers" : "un fichier"; const formats = [...new Set(extensions.map(formatName))]; return `Ajouter ${quantity} ${formats.join(" ou ")}`; }
function selectedFormatLabel(extension: string) { if (!extension) return "Ce fichier"; if (extension === ".zip") return "Une archive ZIP"; return `Un fichier ${formatName(extension)}`; }
function acceptedFormatsLabel(extensions: string[]) { return extensions.map((extension) => `${formatName(extension)} (${extension})`).join(", "); }
function formatName(extension: string) { if (extension === ".xlsx" || extension === ".xls") return "Excel"; if (extension === ".csv") return "CSV"; if (extension === ".zip") return "ZIP"; if (extension === ".json") return "JSON"; return extension.replace(".", "").toUpperCase(); }
function uploadActionLabel(pending: boolean, replacement: ManagedSource | null) { if (pending) return "Import en cours…"; if (replacement) return "Remplacer dans une nouvelle révision"; return "Importer"; }
function Summary({ label, value }: { label: string; value: string }) { return <div className="dm-summary"><dt>{label}</dt><dd>{value}</dd></div>; }
function formatBytes(value: number) { if (!Number.isFinite(value)) return "—"; if (value < 1024) return `${value} o`; return `${(value / 1024 / 1024).toFixed(2)} Mo`; }
function formatDate(value: string) { return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)); }

function effectiveAnalysisState(
  role: string,
  data: SourcesResponse,
  session: ReturnType<typeof useImportQueue>["session"],
): SourceAnalysisState | undefined {
  if (session?.role === role && session.state !== "completed") return "importing";
  if (session?.role === role && session.analysis) {
    if (["queued", "running", "retry_wait", "cancel_requested"].includes(session.analysis.state)) return "running";
    if (session.analysis.state === "succeeded") return "succeeded";
    if (["failed", "cancelled"].includes(session.analysis.state)) return "invalid";
  }
  return data.readiness?.analysisByRole?.[role];
}

function analysisProgress(role: string, session: ReturnType<typeof useImportQueue>["session"]) {
  if (session?.role !== role || !session.analysis) return null;
  const step = session.analysis.step ?? "";
  const counts = step.match(/analyze_reports:(\d+)\/(\d+)/);
  if (counts) return `${counts[1]} / ${counts[2]}`;
  if (["queued", "running", "retry_wait", "cancel_requested"].includes(session.analysis.state)) {
    return `${session.analysis.progress} %`;
  }
  return null;
}

function processingAnalysisProgress(processing: ExplorerProps["activeProcessing"]) {
  if (!processing) return null;
  const counts = processing.step?.match(/analyze_reports:(\d+)\/(\d+)/);
  if (counts) return `${counts[1]} / ${counts[2]}`;
  return `${processing.progress} %`;
}

function stickyAnalysisLabel(data: SourcesResponse, session: ReturnType<typeof useImportQueue>["session"]) {
  if (session && session.state !== "completed") return `Import : ${session.progress} %`;
  if (session?.analysis && ["queued", "running", "retry_wait", "cancel_requested"].includes(session.analysis.state)) {
    return `Analyse : ${session.analysis.progress} %`;
  }
  const states = Object.values(data.readiness?.analysisByRole ?? {});
  if (states.includes("invalid")) return "Analyse invalide";
  if (states.includes("pending")) return "Analyse requise";
  if (states.length && states.every((state) => state === "succeeded")) return "Analyse réussie";
  return "Analyse non demandée";
}

function csvCell(value: string) {
  return `"${value.replaceAll('"', '""')}"`;
}
